import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData, useLocation, useRouteLoaderData } from "react-router";
import clsx from "clsx";
import { useEffect } from "react";
import dateStyles from "react-date-range/dist/styles.css?url"; // main style file
import dateRangeTheme from "react-date-range/dist/theme/default.css?url"; // theme css file
import { Provider } from "react-redux";
import { useTranslation } from "react-i18next";

import i18nServer from "./i18n/i18next.server";
import { DEFAULT_LANGUAGE } from "./i18n/languages";
import { CurrencyProvider } from "./hooks/use-active-currency";
import { getSession } from "./services/session.server";
import { USE_NATS_TRANSPORT } from "./config/nats/env.server";
import "driver.js/dist/driver.css?url";
import { HyperToast } from "./components/ui/sonner";
import { usefetchBrowserInfo } from "./hook/use-browser-info";
import useDarkmode from "./hook/use-dark-mode";
import { useDeviceFingerPrint } from "./hook/use-device-finger-print";
import { attachFingerprintHeader } from "./utils/attach-fingerprint-header.client";
import store from "./store";
import "./styles/css/tailwind.css";
import "./styles/css/shared.css";

import type { Route } from "./+types/root";

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Allura:wght@400;600;700&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;0,900;1,300;1,400;1,700;1,900&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,100..900;1,100..900&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Comfortaa:wght@300..700&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300..700&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap" },
  { rel: "stylesheet", href: "https://fonts.cdnfonts.com/css/segoe-ui-4" },
  { rel: "stylesheet", href: dateStyles },
  { rel: "stylesheet", href: dateRangeTheme },
];

interface Loader {
  isUnderMaintenance: boolean;
  locale: string;
  currency: string | null;
}

export const loader = async ({ request }: Route.LoaderArgs) => {
  const { IS_UNDER_MAINTENANCE } = process.env!;
  // App-controlled locale resolved server-side from userData.preferredLanguage (session).
  let locale = await i18nServer.getLocale(request);

  // If the operator is not multilingual-eligible, force English regardless of user preference.
  // isMultilingualEligible is cached in userData at login time (auth.server.ts) — no DB hit needed.
  // Deliberate staleness tradeoff: this root loader runs on every navigation app-wide,
  // so we read the session-cached flag rather than re-fetching operator config here.
  // If an admin revokes multilingual eligibility mid-session, the affected user keeps
  // seeing their previously-selected non-English locale until they next log in (the
  // language SWITCHER itself is hidden immediately — layout.server.ts fetches fresh
  // operator config on every render — only this locale-forcing fallback lags). The
  // exposure is limited to translated UI strings, not data or permissions, so the
  // staleness window is accepted rather than paying a DB hit on every page transition.
  // Read the session-cached currency here too, so the very first server render
  // and the client's hydration render use the SAME value — avoiding the
  // text-hydration mismatch that a client-only Zustand seed would otherwise cause.
  let currency: string | null = null;
  try {
    const session = await getSession(request.headers.get("Cookie"));
    const userData = session.get("userData") as { isMultilingualEligible?: boolean; currency?: string | null } | undefined;
    if (userData && !userData.isMultilingualEligible) {
      locale = DEFAULT_LANGUAGE;
    }
    currency = userData?.currency ?? null;
  } catch {
    // Non-fatal: fall through with the resolved locale/currency.
  }

  return {
    isUnderMaintenance: IS_UNDER_MAINTENANCE === "true",
    locale,
    currency,
    useNatsTransport: USE_NATS_TRANSPORT,
  };
};

// Namespaces hydrated on the initial render. `getInitialNamespaces()` (client
// entry) reads this so the first paint has these catalogs without a fetch.
// "nav" and "operators" are here (not on a leaf route) because the megabar/
// sidebar/header chrome that uses them renders on every route via _layout —
// without them here, react-i18next's Suspense default throws on first paint.
export const handle = { i18n: ["common", "dashboard", "nav", "operators"] };

// Layout wrapper that provides the HTML structure
// Note: Redux hooks are used inside LayoutInner which is rendered within the Provider
export const Layout = ({ children }: { children: React.ReactNode }) => (
  <Provider store={store}>
    <LayoutInner>{children}</LayoutInner>
  </Provider>
);

// Inner layout component that can use Redux hooks
const LayoutInner = ({ children }: { children: React.ReactNode }) => {
  const { theme } = useDarkmode();
  useDeviceFingerPrint();
  // See attach-fingerprint-header.client.ts — feeds the device fingerprint
  // into the activity-log middleware's request logging.
  useEffect(() => {
    attachFingerprintHeader(store);
  }, []);
  const { userAgent } = usefetchBrowserInfo();
  const { i18n } = useTranslation();
  // undefined on the error boundary render — falls back to MQTT there.
  const rootData = useRouteLoaderData<typeof loader>("root");

  return (
    <html lang={i18n.language} dir={i18n.dir(i18n.language)} className={clsx(theme)}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* Runtime MQTT/NATS switch for getPubSubClient() (pubsub-client.ts). */}
        <script dangerouslySetInnerHTML={{ __html: `window.__USE_NATS_TRANSPORT__=${rootData?.useNatsTransport === true}` }} />
      </head>
      {/* font-segoe comes default in window when explicitly mentioned it overwrites the font */}
      {/* suppressHydrationWarning: userAgent differs between server and client */}
      <body suppressHydrationWarning className={`bg-background text-foreground overflow-hidden ${userAgent?.toString()?.toLocaleLowerCase()?.includes("macintosh") ? "font-segoe" : ""}`}>
        <HyperToast richColors pauseWhenPageIsHidden />
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
};

const App = () => {
  const { isUnderMaintenance, locale, currency } = useLoaderData() as Loader;
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  // Apply the server-resolved language whenever `locale` changes (covers both
  // first mount AND the user-switch case: User A logs out → User B logs in →
  // the root loader re-runs → a new `locale` arrives → we must switch language).
  // Always overwrite localStorage so the previous user's stored language never
  // bleeds into the next user's session.
  // NOTE: i18n is intentionally excluded from deps — it is a stable singleton and
  // including it causes the effect to re-fire on every render, overwriting the
  // client-side language switch with the server locale.
  useEffect(() => {
    if (!locale) return;
    localStorage.setItem("i18nextLng", locale);
    if (i18n.language !== locale) i18n.changeLanguage(locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (isUnderMaintenance && pathname !== "/undermaintenance") {
      window.location.href = "/undermaintenance";
    }
  }, [isUnderMaintenance, pathname]);

  return (
    <CurrencyProvider currency={currency}>
      <Outlet />
    </CurrencyProvider>
  );
};

export default App;
